/* eslint-disable no-empty */
import { makeAutoObservable, observable, reaction, runInAction } from 'mobx';
import keyBy from 'lodash/keyBy';
import services from '../services';
import { extractErrorMessage } from '../utils/helpers';
import { generateLoadEntity, generateLoadList, generateUpdateEntity } from '../utils/mobx';

class ListsStore {
    lists = observable([]);
    list = null;
    deletingItems = [];

    loadingLists = false;
    loadingList = false;
    savingList = false;
    editingList = undefined;
    error = undefined;

    constructor(userStore, commonStore, mainStore) {
        makeAutoObservable(this);
        this.userStore = userStore;
        this.commonStore = commonStore;
        this.mainStore = mainStore;
        if (mainStore) {
            reaction(
                () => this.mainStore.currentCompany,
                () => {
                    runInAction(() => {
                        this.lists.replace([]);
                        this.loadingLists = false;
                    });
                }
            );
        }
    }

    setError(error, type) {
        switch (type) {
            default:
                this.error = error;
                break;
        }
    }
    clearError() {
        this.error = null;
    }

    addOrUpdateList(list) {
        list.link = `/users/list/${list.id}`;
        let index = this.lists.findIndex((l) => l.id === list.id);
        if (index > -1) Object.assign(this.lists[index], list);
        else this.lists.push(list);
    }

    loadCompanyLists = generateLoadList(
        'loadCompanyLists',
        this,
        'loadingLists',
        (companyId) =>
            services.Companies.listsService(companyId).list(),
        'lists',
        (result) => {
            (result || []).forEach(
                (list) => (list.link = `/users/list/${list.id}`)
            );
            return result || [];
        },
    );

    async loadList(companyId, listId) {
        if (this.loadingList) return;
        this.loadingList = true;
        this.list = undefined;
        try {
            this.editingList = await services.Companies.listsService(
                companyId
            ).fetch(listId);
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.loadingList = false;
        }
    }

    loadSingleList = generateLoadEntity(
        'loadList',
        this,
        'loadingList',
        async (companyId, listId) => {
            const result = await services.Companies.listsService(
                companyId
            ).fetch(listId);
            return result;
        },
        'list'
    );

    async delete(companyId, listId) {
        this.deletingItems.push(listId);
        try {
            const result = await services.Companies.listsService(
                companyId
            ).delete(listId);
            let list = this.lists.find((x) => x.id === listId);
            if (list) this.lists.remove(list);
            return result;
        } catch (e) {
            this.error = extractErrorMessage(e);
        } finally {
            this.deletingItems.remove(listId);
        }
    }

    save = generateUpdateEntity(
        'saveList',
        this,
        'savingList',
        async (companyId, listId, values) => {
            const svc = services.Companies.listsService(companyId);

            if (this.editingList && listId) {
                await svc.update({
                    id: this.editingList.id,
                    ...values,
                });
                this.commonStore.analyticsEvent(
                    'lists',
                    'update',
                    `company-${companyId}`
                );
            } else {
                const result = await svc.create({ active: 1, ...values });
                this.commonStore.analyticsEvent(
                    'lists',
                    'create',
                    `company-${companyId}`
                );
                listId = result.insertId;
            }

            // fetch fresh and merge into store
            const list = await svc.fetch(listId);

            list.link = `/users/list/${list.id}`;

            const idx = this.lists.findIndex((l) => l && l.id === list.id);
            if (idx > -1) {
                Object.assign(this.lists[idx], list);
            } else {
                this.lists.push(list);
            }

            if (this.list && this.list.id === list.id) {
                Object.assign(this.list, list);
            }

            this.editingList = null;
            return listId;
        }
    );


    get allListsIndex() {
        return keyBy(this.lists, 'id');
    }

    get listsWithUsers() {
        let result = [];
        this.lists.forEach((list) => {
            let usersDetails = this.userStore.accumulatedIndex[list.id];
            result.push({
                ...list,
                users: usersDetails ? usersDetails.users : [],
            });
        });
        return result;
    }
}

export default ListsStore;
